import React, { useState, useEffect } from 'react';
import { initializeApp } from "firebase/app";
import { getFirestore, collection, query, addDoc, serverTimestamp, orderBy, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import './HRDashboard.css';

// Messaging Firebase konfigurasjon
const messagingFirebaseConfig = {
  apiKey: "AIzaSyD3wtWhRNNsh0I2xG2YhWh2Hu18XszK53Y",
  authDomain: "purepaths.firebaseapp.com",
  databaseURL: "https://purepaths-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "purepaths",
  storageBucket: "purepaths.appspot.com",
  messagingSenderId: "361545361366",
  appId: "1:361545361366:web:08db6f5e8ef28731eae677",
  measurementId: "G-QF7CY19PH7"
};

// Initialiser messaging Firebase
const messagingApp = initializeApp(messagingFirebaseConfig, 'messaging');
const messagingDb = getFirestore(messagingApp);

function HRDashboard() {
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const [newReply, setNewReply] = useState('');
  const user = JSON.parse(localStorage.getItem('user'));
  const companyName = user?.companyName;
  const [activeTab, setActiveTab] = useState('messages'); // 'messages' eller 'posts'
  const [newPost, setNewPost] = useState({
    title: '',
    text: '',
    image: null
  });
  const [posts, setPosts] = useState([]);
  const [hasMessages, setHasMessages] = useState(false);

  useEffect(() => {
    if (!companyName) {
      console.log('Ingen companyName funnet i user:', user);
      return;
    }

    console.log('Setter opp live-lytting for meldinger');
    
    // Array med invitasjonskoder vi vil lytte på
    const invitationCodes = ['BH2023STORD5', 'BH2023STORD1'];
    const unsubscribers = [];

    // Sett loading til true når vi starter lastingen
    setLoading(true);

    // Sett opp lyttere for hver invitasjonskode
    invitationCodes.forEach(invCode => {
      // Hent meldinger for hver invitasjonskode
      const messagesRef = collection(messagingDb, 'HRmessages', 'Baker Hughes Stord', 'invitationCodes', invCode, 'messages');
      const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'));
      
      // Lytt på meldinger i sanntid
      const unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
        try {
          const userMessages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            type: 'user_message',
            timestamp: doc.data().timestamp?.toDate() || new Date(),
            invitationCode: invCode // Legg til invitasjonskode i meldingsobjektet
          }));

          setMessages(prevMessages => {
            const currentReplies = (prevMessages[invCode] || [])
              .filter(msg => msg.type === 'hr_reply');
            
            const allMessages = [...userMessages, ...currentReplies].sort((a, b) => 
              (a.timestamp?.getTime() || 0) - (b.timestamp?.getTime() || 0)
            );

            return {
              ...prevMessages,
              [invCode]: allMessages
            };
          });

          if (userMessages.length > 0) {
            setHasMessages(true);
          }
        } catch (error) {
          console.error('Error processing messages:', error);
        }
      });

      // Hent HR-svar for hver invitasjonskode
      const repliesRef = collection(messagingDb, 'HRmessages', 'Baker Hughes Stord', 'invitationCodes', invCode, 'hr_replies');
      const repliesQuery = query(repliesRef, orderBy('timestamp', 'asc'));

      const unsubscribeReplies = onSnapshot(repliesQuery, (snapshot) => {
        try {
          const hrReplies = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            type: 'hr_reply',
            timestamp: doc.data().timestamp?.toDate() || new Date(),
            invitationCode: invCode
          }));

          setMessages(prevMessages => {
            const currentMessages = (prevMessages[invCode] || [])
              .filter(msg => msg.type === 'user_message');
            
            const allMessages = [...currentMessages, ...hrReplies].sort((a, b) => 
              (a.timestamp?.getTime() || 0) - (b.timestamp?.getTime() || 0)
            );

            return {
              ...prevMessages,
              [invCode]: allMessages
            };
          });
        } catch (error) {
          console.error('Error processing HR replies:', error);
        }
      });

      unsubscribers.push(unsubscribeMessages, unsubscribeReplies);
    });

    // Sett loading til false når vi er ferdig med å sette opp lytterne
    setLoading(false);

    // Cleanup function
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [companyName, user]);

  useEffect(() => {
    const postsRef = collection(messagingDb, 'posts');
    const q = query(
      postsRef, 
      orderBy('timestamp', 'desc')
    );
    
    const unsubscribePosts = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate()
        }))
        .filter(post => post.company === companyName);
      
      setPosts(postsData);
    });

    return () => unsubscribePosts();
  }, [companyName]);

  const handleSendReply = async (invitationCode, replyText) => {
    if (!replyText.trim() || !companyName) return;

    try {
      const replyData = {
        message: replyText,
        timestamp: serverTimestamp(),
        hrEmail: user.email
      };

      // Bruk messagingDb for å lagre svar
      await addDoc(
        collection(messagingDb, 'HRmessages', companyName, 'invitationCodes', invitationCode, 'hr_replies'),
        replyData
      );

      console.log('HR reply sent successfully to:', invitationCode);
      setNewReply('');
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Kunne ikke sende svar. Prøv igjen senere.');
    }
  };

  // Legg til denne funksjonen for å håndtere bildeopplasting
  const handleImageUpload = async (file) => {
    const storage = getStorage(messagingDb);
    const imageRef = ref(storage, `posts/${companyName}/${Date.now()}_${file.name}`);
    
    try {
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let imageUrl = null;
      if (newPost.image) {
        imageUrl = await handleImageUpload(newPost.image);
      }

      await addDoc(collection(messagingDb, 'posts'), {
        title: newPost.title,
        text: newPost.text,
        imageUrl: imageUrl,
        company: companyName,
        timestamp: serverTimestamp(),
        author: user.email
      });

      console.log('Innlegg publisert med følgende data:', {
        title: newPost.title,
        text: newPost.text,
        imageUrl: imageUrl,
        company: companyName,
        author: user.email
      });

      setNewPost({ title: '', text: '', image: null });
      alert('Innlegg publisert!');
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Kunne ikke publisere innlegg. Prøv igjen senere.');
    }
  };

  const renderPosts = () => (
    <div className="posts-section">
      <h2>Opprett nytt innlegg</h2>
      <form onSubmit={handlePostSubmit} className="post-form">
        <input
          type="text"
          placeholder="Tittel"
          value={newPost.title}
          onChange={(e) => setNewPost(prev => ({ ...prev, title: e.target.value }))}
          required
        />
        
        <textarea
          placeholder="Tekst"
          value={newPost.text}
          onChange={(e) => setNewPost(prev => ({ ...prev, text: e.target.value }))}
          required
          rows="4"
        />
        
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setNewPost(prev => ({ ...prev, image: e.target.files[0] }))}
        />
        
        <button type="submit" className="publish-button">
          Publiser innlegg
        </button>
      </form>

      <div className="posts-list">
        <h2>Dine innlegg</h2>
        {posts.map(post => (
          <div key={post.id} className="post-card">
            <h3>{post.title}</h3>
            <p>{post.text}</p>
            {post.imageUrl && (
              <img 
                src={post.imageUrl} 
                alt={post.title}
                className="post-image" 
              />
            )}
            <div className="post-meta">
              <span>{post.timestamp?.toLocaleString('no-NO')}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMessages = () => {
    if (loading) {
      return <div className="loading">Laster meldinger...</div>;
    }

    if (!hasMessages) {
      return <div className="no-messages">Ingen meldinger ennå</div>;
    }

    return (
      <div className="messages-container">
        {Object.entries(messages).map(([anonymId, messageList]) => {
          // Sjekk om det finnes bruker-meldinger (ikke bare HR-svar)
          const hasUserMessages = messageList.some(msg => msg.type === 'user_message');
          
          // Vis bare chat-boksen hvis det finnes bruker-meldinger
          if (!hasUserMessages) {
            return null;
          }

          const uniqueMessages = messageList.reduce((acc, current) => {
            const isDuplicate = acc.find(item => 
              item.message === current.message && 
              item.timestamp?.getTime() === current.timestamp?.getTime()
            );
            if (!isDuplicate) {
              acc.push(current);
            }
            return acc;
          }, []);

          const lastMessage = uniqueMessages[uniqueMessages.length - 1];

          return (
            <div key={anonymId} className="chat-thread">
              <div className="chat-header">
                <span className="anonym-id">{anonymId}</span>
                <span className="timestamp">
                  {lastMessage?.timestamp?.toLocaleString('no-NO') || 'Ukjent tid'}
                </span>
              </div>
              
              <div className="chat-messages">
                {uniqueMessages.map((message) => (
                  <div 
                    key={message.id} 
                    className={message.type === 'hr_reply' ? "hr-message" : "user-message"}
                  >
                    <p>{message.message}</p>
                    <span className="message-timestamp">
                      {message.timestamp?.toLocaleString('no-NO') || 'Ukjent tid'}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="message-input">
                <input 
                  type="text" 
                  placeholder="Skriv meldingen din..."
                  value={newReply}
                  onChange={(e) => setNewReply(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSendReply(anonymId, newReply);
                    }
                  }}
                />
                <button 
                  className="send-button"
                  onClick={() => handleSendReply(anonymId, newReply)}
                >
                  Send
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="hr-dashboard">
      <div className="tabs">
        <button 
          className={activeTab === 'messages' ? 'active' : ''} 
          onClick={() => setActiveTab('messages')}
        >
          Meldinger
        </button>
        <button 
          className={activeTab === 'posts' ? 'active' : ''} 
          onClick={() => setActiveTab('posts')}
        >
          Innlegg
        </button>
      </div>

      {activeTab === 'messages' ? renderMessages() : renderPosts()}
    </div>
  );
}

export default HRDashboard; 